/**
 * Portal Announcements Page
 * Manage store announcements/ads (scrolling text banner)
 */

import React, { useEffect, useState } from 'react';
import portalService from '../../services/portal.service';

const PortalAnnouncements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [texto, setTexto] = useState('');
  const [corFundo, setCorFundo] = useState('linear-gradient(90deg, #7c3aed, #14b8a6)');
  const [corTexto, setCorTexto] = useState('#ffffff');

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await portalService.getAnnouncements();
      setAnnouncements(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editing) {
        await portalService.updateAnnouncement(editing.ID, { texto, corFundo, corTexto });
      } else {
        await portalService.createAnnouncement({ texto, corFundo, corTexto });
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Eliminar este anuncio?')) return;
    try {
      await portalService.deleteAnnouncement(id);
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleToggle = async (item: any) => {
    try {
      await portalService.updateAnnouncement(item.ID, { ativo: item.ATIVO ? 0 : 1 });
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const resetForm = () => {
    setEditing(null);
    setTexto('');
    setCorFundo('linear-gradient(90deg, #7c3aed, #14b8a6)');
    setCorTexto('#ffffff');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Publicidade & Novidades</h1>
          <p className="text-gray-600">Mensagens que aparecem no topo da sua loja (scrolling horizontal)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Anuncio
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">Fechar</button>
        </div>
      )}

      {/* Preview */}
      {announcements.filter(a => a.ATIVO).length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm font-medium text-gray-500 mb-2">Pre-visualizacao:</p>
          <div
            className="rounded-lg overflow-hidden py-2 px-4"
            style={{ background: announcements.find(a => a.ATIVO)?.COR_FUNDO || 'linear-gradient(90deg, #7c3aed, #14b8a6)' }}
          >
            <div className="whitespace-nowrap overflow-hidden">
              <span
                className="inline-block animate-marquee text-sm font-medium"
                style={{ color: announcements.find(a => a.ATIVO)?.COR_TEXTO || '#fff' }}
              >
                {announcements.filter(a => a.ATIVO).map(a => a.TEXTO).join('  •  ')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {announcements.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
            <p className="text-lg mb-2">Nenhum anuncio criado</p>
            <p className="text-sm">Crie mensagens para os clientes verem quando entram na sua loja.</p>
          </div>
        ) : (
          announcements.map((item) => (
            <div key={item.ID} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${item.ATIVO ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-xs text-gray-500">{item.ATIVO ? 'Ativo' : 'Inativo'}</span>
                  </div>
                  <p className="text-gray-900 font-medium">{item.TEXTO}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span
                      className="inline-block w-20 h-6 rounded text-xs text-center leading-6"
                      style={{ background: item.COR_FUNDO, color: item.COR_TEXTO }}
                    >
                      Cor
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleToggle(item)}
                    className={`px-3 py-1.5 text-xs rounded-lg ${item.ATIVO ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'}`}
                  >
                    {item.ATIVO ? 'Desativar' : 'Ativar'}
                  </button>
                  <button
                    onClick={() => { setEditing(item); setTexto(item.TEXTO); setCorFundo(item.COR_FUNDO); setCorTexto(item.COR_TEXTO); setShowModal(true); }}
                    className="px-3 py-1.5 text-xs text-pagali-primary hover:bg-pagali-primary/5 rounded-lg"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(item.ID)}
                    className="px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">{editing ? 'Editar Anuncio' : 'Novo Anuncio'}</h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Texto do Anuncio *</label>
                <textarea
                  required value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={3}
                  placeholder="Ex: Promocao de Verao! 20% desconto em todos os oculos de sol..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cor de Fundo</label>
                <input
                  type="text" value={corFundo}
                  onChange={(e) => setCorFundo(e.target.value)}
                  placeholder="linear-gradient(90deg, #7c3aed, #14b8a6)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
                />
                <p className="text-xs text-gray-500 mt-1">Aceita gradientes CSS ou cores simples (#hex)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cor do Texto</label>
                <div className="flex gap-2">
                  <input
                    type="color" value={corTexto}
                    onChange={(e) => setCorTexto(e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300"
                  />
                  <input
                    type="text" value={corTexto}
                    onChange={(e) => setCorTexto(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              {/* Preview */}
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Pre-visualizacao:</p>
                <div className="rounded-lg py-2 px-4" style={{ background: corFundo }}>
                  <p className="text-sm font-medium truncate" style={{ color: corTexto }}>{texto || 'O seu texto aqui...'}</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80">
                  {editing ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalAnnouncements;
